import { useState, useEffect } from "react";
import type { LayoutSection } from "./LayoutSections";
import type { ImagePlacement } from "./ImagePlacementManager";
import { ImagePlacementCard } from "./ImagePlacementCard";
import { getPublicUrl } from "../../lib/utils/storage";

interface ImagePlacementListProps {
  sections: LayoutSection[];
  placements: ImagePlacement[];
  onDelete: (id: number) => Promise<void>;
  onUpdate: () => Promise<void>;
}

interface ImageUrlCache {
  [key: string]: string;
}

export function ImagePlacementList({
  sections,
  placements,
  onDelete,
  onUpdate,
}: ImagePlacementListProps) {
  const [imageUrls, setImageUrls] = useState<ImageUrlCache>({});

  // 画像URLの取得
  useEffect(() => {
    const fetchImageUrls = async () => {
      const newImageUrls: ImageUrlCache = {};

      for (const placement of placements) {
        if (
          placement.illustration.storage_path &&
          !imageUrls[placement.illustration.storage_path]
        ) {
          try {
            const url = await getPublicUrl(
              "thumbnails",
              placement.illustration.storage_path
            );
            newImageUrls[placement.illustration.storage_path] = url;
          } catch (error) {
            console.error("画像URLの取得に失敗しました:", error);
          }
        }
      }

      setImageUrls((prev) => ({ ...prev, ...newImageUrls }));
    };

    fetchImageUrls();
  }, [placements]);

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-center text-gray-700">
        配置済み画像・アイコン一覧
      </h2>
      <div className="space-y-4">
        {sections.map((section) => {
          const sectionPlacements = placements.filter(
            (p) => p.section_id === section.id
          );
          if (sectionPlacements.length === 0) return null;

          const maxOrder = Math.max(
            ...sectionPlacements.map((p) => p.sort_order),
            0
          );

          return (
            <div key={section.id} className="border rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {section.section_name}
                <span className="text-sm text-gray-500 ml-2">
                  ({sectionPlacements.length} / {section.max_images})
                </span>
              </h3>
              <div className="grid grid-cols-1 gap-4">
                {sectionPlacements.map((placement) => (
                  <ImagePlacementCard
                    key={placement.id}
                    placement={placement}
                    imageUrl={imageUrls[placement.illustration.storage_path]}
                    onDelete={onDelete}
                    onUpdate={onUpdate}
                    maxOrder={maxOrder}
                    sectionPlacements={sectionPlacements}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
